/**
 * **영토의 수비대**(Todo9 5단계). 내 나라 땅(수도 빼고)에 키운 병사를 두고, 동료 하나를 수비 대장으로 세울 수 있다. 둔 땅은
 * 차례마다 군자금을 낸다(`realmTick`). 각 나라 장수가 그 땅을 치러 오는 것(수비 판정·부상·전사)은 다음 단계다.
 *
 * **둔 병사는 계정의 병사 목록(`Army.soldiers`)에서 땅으로 옮긴다**(`Garrison.soldiers`). 그러면 출정 편성·자동 편성·급료·
 * 진급 화면이 저절로 수비 중인 병사를 보지 않는다 — 목록에 남겨 두고 곳곳에서 걸렀다면 한 곳을 빠뜨리는 날 수비 중인
 * 병사가 출정에도 나갔을 것이다. 거두면 목록으로 돌아온다(uid·이름·경험치 그대로).
 *
 * 수비 대장은 동료의 상태에 적는다(`CompanionState.garrison`). 대장인 동료는 출정에 나서지 못한다(`availableCompanions`).
 * **동료는 죽지 않는다**(사용자 결정) — 땅을 잃으면 쉬러 돌아온다(다음 단계).
 */
import { SQUAD_SIZE, availableCompanions, mateSquadSize } from './army.ts';
import type { Army } from './army.ts';
import { STAKE_MIN, TERRITORIES, TERRITORY_BY_ID, garrisonDepth } from './realm.ts';
import type { Garrison, RealmState } from './realm.ts';

/** 수비대를 둘 수 있는 땅인가: 내 나라 땅이고 수도가 아니다. 수도는 점령되지 않으므로 지킬 까닭이 없다. */
export function canGarrison(realm: RealmState, culture: number, id: string): boolean {
  const t = TERRITORY_BY_ID.get(id);
  return t !== undefined && !t.capital && realm.owner[id] === culture;
}

export function garrisonOf(realm: RealmState, id: string): Garrison | null {
  return realm.garrisons?.[id] ?? null;
}

/**
 * 한 땅에 둘 수 있는 병사 수. 대장이 있으면 그 동료가 이끄는 분대의 정원(`mateSquadSize`), 없으면 기본 분대 정원(`SQUAD_SIZE`, 지금 14) —
 * 대장이 있어야 더 많이 둔다. 출정의 분대와 같은 눈금이다.
 */
export function garrisonCap(army: Army, captain: string | null): number {
  return captain ? mateSquadSize(army, captain) : SQUAD_SIZE;
}

/** 편성(`Army.formation`)에서 그 병사·동료를 지운다 — 땅으로 간 사람이 다음 출정 화면에 빈자리로 남지 않게. */
function dropFromFormation(army: Army, uids: ReadonlySet<number>, mate: string | null): void {
  const f = army.formation;
  if (!f) return;
  f.squads = f.squads.map((list) => list.filter((u) => !uids.has(u)));
  if (mate) {
    f.companions = f.companions.map((c) => (c === mate ? null : c));
    if (f.escort === mate) f.escort = null;
  }
}

/**
 * 병사를 그 땅에 둔다. 둘 수 없는 땅이거나 정원을 넘으면 넘는 만큼은 두지 않는다. 둔 수를 돌려준다.
 */
export function deploySoldiers(army: Army, realm: RealmState, id: string, uids: readonly number[]): number {
  if (!canGarrison(realm, army.culture, id)) return 0;
  realm.garrisons ??= {};
  const g = (realm.garrisons[id] ??= { soldiers: [], captain: null });
  const room = garrisonCap(army, g.captain) - g.soldiers.length;
  const before = g.soldiers.length;
  const want = new Set(uids);
  const moving = army.soldiers.filter((s) => want.has(s.uid)).slice(0, Math.max(0, room));
  if (moving.length === 0) return 0;
  const moved = new Set(moving.map((s) => s.uid));
  army.soldiers = army.soldiers.filter((s) => !moved.has(s.uid));
  g.soldiers.push(...moving);
  // 최소 투입을 이번에 채웠으면 그 차례부터 센다(`Garrison.since`).
  if (before < STAKE_MIN && g.soldiers.length >= STAKE_MIN) g.since = realm.turn;
  dropFromFormation(army, moved, null);
  return moving.length;
}

/** 그 땅의 수비대를 모두 거둔다(병사는 목록으로, 대장은 풀린다). 땅이 비면 지운다. */
export function recallGarrison(army: Army, realm: RealmState, id: string): number {
  const g = garrisonOf(realm, id);
  if (!g) return 0;
  army.soldiers.push(...g.soldiers);
  army.soldiers.sort((a, b) => a.uid - b.uid);
  if (g.captain) {
    const st = army.companions.find((c) => c.id === g.captain);
    if (st) delete st.garrison;
  }
  const n = g.soldiers.length;
  delete realm.garrisons![id];
  return n;
}

/**
 * 수비 대장을 세운다(null이면 내린다). 출정에 나설 수 있는 동료만(요양 중이거나 다른 땅의 대장이면 안 된다). 대장이 바뀌어
 * 정원이 줄면 넘는 병사는 목록으로 돌아온다.
 */
export function setCaptain(army: Army, realm: RealmState, id: string, mate: string | null): boolean {
  if (!canGarrison(realm, army.culture, id)) return false;
  if (mate !== null && !availableCompanions(army).some((d) => d.id === mate)) return false;
  realm.garrisons ??= {};
  const g = (realm.garrisons[id] ??= { soldiers: [], captain: null });
  if (g.captain) {
    const old = army.companions.find((c) => c.id === g.captain);
    if (old) delete old.garrison;
  }
  g.captain = mate;
  if (mate) {
    army.companions.find((c) => c.id === mate)!.garrison = id;
    dropFromFormation(army, new Set(), mate);
  }
  const cap = garrisonCap(army, mate);
  if (g.soldiers.length > cap) {
    army.soldiers.push(...g.soldiers.splice(cap));
    army.soldiers.sort((a, b) => a.uid - b.uid);
  }
  if (g.soldiers.length === 0 && g.captain === null) delete realm.garrisons[id];
  return true;
}

/**
 * 한 땅이 한 차례에 내는 군자금: 둔 병사 한 명에 2냥(`SQUAD_SIZE`명까지 셈 — 지금 14, 정원이 열둘이던 때는 12) + 그 땅의 수비 깊이(수도에 가까운 땅일수록 값지다).
 * 대장이 있으면 ×1.25. 병사가 없으면 0 — 대장만 세워 두는 것으로는 거두지 못한다.
 */
export function territoryIncome(realm: RealmState, id: string): number {
  const g = garrisonOf(realm, id);
  if (!g || g.soldiers.length === 0) return 0;
  const base = Math.min(g.soldiers.length, SQUAD_SIZE) * 2 + garrisonDepth(id);
  return g.captain ? Math.floor((base * 125) / 100) : base;
}

/**
 * 모든 수비대의 한 차례 수입. 땅이 늘수록 한 곳의 몫이 준다(n곳이면 100 ÷ (100 + 15·(n−1)) — 눈덩이를 막는다,
 * Todo9 함정 5). 땅 순서로 센다.
 */
export function realmIncome(realm: RealmState): { total: number; places: number } {
  let sum = 0;
  let places = 0;
  for (const t of TERRITORIES) {
    const v = territoryIncome(realm, t.id);
    if (v > 0) {
      sum += v;
      places++;
    }
  }
  if (places === 0) return { total: 0, places: 0 };
  return { total: Math.floor((sum * 100) / (100 + 15 * (places - 1))), places };
}

